import React from 'react'

import { Controller } from 'react-hook-form'

import {
  Button,
  TextInput,
  Checkbox,
  Icon,
  VisibilityOn,
  VisibilityOff,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import PropTypes from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import FacebookIcon from 'Pods/Shared/Icons/FacebookIcon'
import GoogleIcon from 'Pods/Shared/Icons/GoogleIcon'

import MTCaptcha from 'Utils/MTCaptcha'

import { useLogin } from './Hooks/useLogin'

function Login({ customId = '', captchaId = '', locale = 'en' }) {
  const translate = (key) => I18n.t(key, { ns: 'rails', lng: locale })

  const {
    token,
    control,
    errors,
    touchedFields,
    showPassword,
    twoFASecondState,
    twoFaCode,
    loginCaptchaEnabled,
    remember,
    handleSubmit,
    onFormSubmit,
    onTwoFaSubmit,
    onTwoFaCodeChange,
    onRememberToggle,
    onShowPasswordToggle,
  } = useLogin({
    captchaId,
    locale,
  })

  return (
    <div>
      {!twoFASecondState && (
        <div className="user-auth-container">
          <div className="social-buttons-container social-buttons-container--top">
            <div className="row">
              <div className="col-6">
                <form action="/users/auth/google_oauth2" method="post">
                  <Button
                    expand
                    size="large"
                    icon={<GoogleIcon />}
                    type="primary"
                    variant="only-icon"
                    customClass="cgt-button--google-plus justify-content-center"
                  />
                  {token && <input type="hidden" name="authenticity_token" value={token} />}
                </form>
              </div>
              <div className="col-6">
                <form action="/users/auth/facebook" method="post">
                  <Button
                    expand
                    size="large"
                    icon={<FacebookIcon />}
                    type="primary"
                    variant="only-icon"
                    customClass="cgt-button--facebook justify-content-center"
                  />
                  {token && <input type="hidden" name="authenticity_token" value={token} />}
                </form>
              </div>
            </div>
          </div>

          <div className="auth-divider">
            <span>{translate('login.divider')}</span>
          </div>

          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: translate('login.validation.email_username_required'),
              }}
              render={({ field }) => (
                <TextInput
                  label={translate('login.email_label')}
                  id={`email${customId}`}
                  name="email"
                  isWarning={touchedFields.email && !!errors.email}
                  helpText={touchedFields.email ? errors.email?.message : ''}
                  value={field.value}
                  wrapperClassName="auth-tab__field"
                  handleChange={field.onChange}
                  onBlur={field.onBlur}
                  isPilot
                  isRequired
                />
              )}
            />
            {/* Honey pot field */}
            <Controller
              name="email_confirmation"
              control={control}
              render={({ field }) => (
                <TextInput
                  label={translate('login.email_confirmation_label')}
                  id={`email_confirmation${customId}`}
                  name="email_confirmation"
                  value={field.value}
                  wrapperClassName="auth-tab__field hidden"
                  handleChange={field.onChange}
                  autoComplete="off"
                  isPilot
                  isRequired
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{
                required: translate('login.validation.password_required'),
              }}
              render={({ field }) => (
                <TextInput
                  label={translate('login.password_label')}
                  id={`password${customId}`}
                  name="password"
                  inputType={showPassword ? 'text' : 'password'}
                  isWarning={touchedFields.password && !!errors.password}
                  helpText={touchedFields.password ? errors.password?.message : ''}
                  value={field.value}
                  wrapperClassName="auth-tab__field"
                  handleChange={field.onChange}
                  onBlur={field.onBlur}
                  isPilot
                  isRequired
                  trailIcon={(
                    <Icon
                      svgComponent={showPassword ? VisibilityOff : VisibilityOn}
                      color="dark"
                      withHover
                      onClick={onShowPasswordToggle}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    />
                  )}
                />
              )}
            />
            <a href="/users/password/new" className="cgt-legacy-link auth-forgot-password">
              {translate('login.forgot_password')}
            </a>
            <Checkbox
              className="form__remember-me"
              checkedColor="primary"
              id={`remember-me${customId}`}
              label={translate('login.remember_me')}
              isChecked={remember}
              onChange={onRememberToggle}
            />

            {loginCaptchaEnabled && <MTCaptcha captchaId={captchaId} />}
            <Button
              expand
              size="large"
              actionType="submit"
            >
              {translate('login.submit_button')}
            </Button>
          </form>
        </div>
      )}
      {twoFASecondState && (
        <div className="user-auth-container">
          <p className="cgt-legacy-heading-h5 u-text-center cgt-mb-20">
            {translate('login.two_fa.title')}
          </p>
          <p className="u-text-center cgt-mb-20">
            {translate('login.two_fa.description')}
          </p>
          <form onSubmit={onTwoFaSubmit}>
            <TextInput
              name="twoFaCode"
              id="twoFaCode"
              wrapperClassName="auth-tab__field"
              isPilot
              isRequired
              value={twoFaCode}
              handleChange={onTwoFaCodeChange}
            />
            <Button
              expand
              actionType="submit"
            >
              {translate('login.two_fa.verify_button')}
            </Button>
          </form>
        </div>
      )}
    </div>
  )
}

Login.propTypes = {
  customId: PropTypes.string,
  captchaId: PropTypes.string,
  locale: PropTypes.string,
}

export default Login
